import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminApi } from '../../api';
import type { AdminReportDto } from '../../api';
import { ReportStatus } from '../../api/generated/boticaAPI.schemas';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { DataGrid, type DataGridItem } from '../../components/ui/DataGrid';
import { Skeleton } from '../../components/ui/Skeleton';
import { useToast } from '../../components/ui/Toast';
import {
  REASON_LABEL,
  STATUS_BADGE,
  STATUS_LABEL,
  TARGET_LABEL,
  formatDateTime,
} from './reportLabels';

/* ─── Reported content ───────────────────────────────────────────────────── */

function ReportedContent({ report }: { report: AdminReportDto }) {
  const content = report.content;

  // A purged message is not a missing one. The retention sweep removed the row
  // on schedule; saying so keeps a reviewer from reading an empty panel as a
  // fault and going to look for a bug that is not there.
  if (content?.isPurged) {
    return (
      <div
        style={{
          padding: 16,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-secondary)',
          fontSize: 'var(--text-sm)',
          lineHeight: 1.6,
        }}
      >
        This message was deleted by its author and has since passed the retention
        window, so its text was permanently removed. Nothing is missing — the
        report itself is kept, but the content is no longer available to review.
      </div>
    );
  }

  if (!content) {
    return (
      <div
        style={{
          padding: 16,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-placeholder)',
          fontSize: 'var(--text-sm)',
        }}
      >
        The reported content no longer exists.
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 16px',
          background: 'var(--warm-gray-100)',
          borderBottom: '1px solid var(--border-subtle)',
          fontSize: 'var(--text-sm)',
        }}
      >
        <span style={{ fontWeight: 500 }}>{content.authorFullName ?? 'Unknown author'}</span>
        <span style={{ color: 'var(--text-secondary)' }}>
          {formatDateTime(content.createdAt)}
        </span>
      </div>

      {/* A message the sender soft-deleted is still shown here — that is what
          soft delete is for. Flag it so the reviewer knows their view differs
          from what the reporter and recipient now see in the thread. */}
      {content.isDeleted && (
        <div
          style={{
            padding: '8px 16px',
            background: 'var(--warm-gray-50)',
            borderBottom: '1px solid var(--border-subtle)',
            color: 'var(--text-secondary)',
            fontSize: 'var(--text-xs)',
          }}
        >
          Deleted by its author after being sent. Still shown here for review; in the
          conversation it now reads “Mensaje eliminado”.
        </div>
      )}

      <div
        style={{
          padding: 16,
          whiteSpace: 'pre-wrap',
          fontSize: 'var(--text-sm)',
          lineHeight: 1.6,
        }}
      >
        {content.body || (
          <span style={{ color: 'var(--text-placeholder)' }}>(no text content)</span>
        )}
      </div>
    </div>
  );
}

/* ─── Review actions ─────────────────────────────────────────────────────── */

function ReviewActions({
  reportId,
  onComplete,
}: {
  reportId: string;
  onComplete: (updated: AdminReportDto) => void;
}) {
  const toast = useToast();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState<'action' | 'dismiss' | null>(null);

  const submit = async (outcome: 'action' | 'dismiss') => {
    setSubmitting(outcome);
    try {
      const updated = await adminApi.reviewReport(reportId, {
        status: outcome === 'action' ? ReportStatus.Actioned : ReportStatus.Dismissed,
        resolutionNote: note.trim() || null,
      });
      toast(
        outcome === 'action' ? 'Report marked as actioned.' : 'Report dismissed.',
        outcome === 'action' ? 'success' : 'neutral',
      );
      onComplete(updated);
    } catch {
      toast('Failed to process action. Try again.', 'error');
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div style={{ marginTop: 24 }}>
      <label
        htmlFor="resolution-note"
        style={{
          display: 'block',
          marginBottom: 6,
          fontSize: 'var(--text-sm)',
          fontWeight: 500,
        }}
      >
        Resolution note
      </label>
      <textarea
        id="resolution-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="What did you do, and why? Recorded against the report."
        style={{
          width: '100%',
          padding: 10,
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)',
          fontSize: 'var(--text-sm)',
          fontFamily: 'inherit',
          resize: 'vertical',
        }}
      />

      <p
        style={{
          margin: '8px 0 0',
          color: 'var(--text-secondary)',
          fontSize: 'var(--text-xs)',
        }}
      >
        Recording an outcome does not remove content or sanction the author — take those
        actions separately.
      </p>

      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <Button
          variant="primary"
          disabled={submitting !== null}
          onClick={() => submit('action')}
        >
          {submitting === 'action' ? 'Saving…' : 'Mark actioned'}
        </Button>
        <Button
          variant="secondary"
          disabled={submitting !== null}
          onClick={() => submit('dismiss')}
        >
          {submitting === 'dismiss' ? 'Saving…' : 'Dismiss'}
        </Button>
      </div>
    </div>
  );
}

/* ─── ReportDetailPage ───────────────────────────────────────────────────── */

export function ReportDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<AdminReportDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    // Fetching this endpoint writes an admin audit entry with the reviewer's
    // IP server-side, because the response carries private content.
    void (async () => {
      try {
        const result = await adminApi.getReportById(id);
        if (!cancelled) setReport(result);
      } catch {
        if (!cancelled) setError('Failed to load report. Try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div style={{ maxWidth: 'var(--content-max-width)' }}>
        <Skeleton height={28} width="40%" />
        <div style={{ marginTop: 16 }}>
          <Skeleton height={120} />
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div style={{ maxWidth: 'var(--content-max-width)' }}>
        <p style={{ color: 'var(--status-unavailable)' }}>{error ?? 'Report not found.'}</p>
        <Button variant="secondary" onClick={() => navigate('/reports')}>
          Back to reports
        </Button>
      </div>
    );
  }

  const status = report.status ?? 0;
  const isPending = status === ReportStatus.Pending;
  const totalForTarget = report.totalReportsForTarget ?? 1;

  const items: DataGridItem[] = [
    { label: 'Target', value: TARGET_LABEL[report.targetType ?? 0] ?? '—' },
    { label: 'Reason', value: REASON_LABEL[report.reason ?? 0] ?? '—' },
    { label: 'Reported by', value: report.reporterFullName ?? '—' },
    { label: 'Filed', value: formatDateTime(report.createdAt) },
    // Shown only when it differs from Filed, i.e. the report was reopened by a
    // re-submission. The queue orders on this, so without it an old Filed date
    // sitting at the top of the list looks like a sorting fault.
    ...(report.lastReportedAt && report.lastReportedAt !== report.createdAt
      ? [{ label: 'Reported again', value: formatDateTime(report.lastReportedAt) }]
      : []),
    {
      label: 'Reports against this target',
      value:
        totalForTarget > 1 ? <Badge variant="pending">{totalForTarget}</Badge> : totalForTarget,
    },
    { label: 'Status', value: <Badge variant={STATUS_BADGE[status]}>{STATUS_LABEL[status]}</Badge> },
  ];

  if (report.details) {
    items.push({ label: 'Reporter’s notes', value: report.details, span: true });
  }

  if (!isPending) {
    items.push(
      { label: 'Reviewed by', value: report.reviewedByFullName ?? '—' },
      { label: 'Reviewed', value: formatDateTime(report.reviewedAt) },
    );
    if (report.resolutionNote) {
      items.push({ label: 'Resolution note', value: report.resolutionNote, span: true });
    }
  }

  return (
    <div style={{ maxWidth: 'var(--content-max-width)' }}>
      <Button variant="secondary" onClick={() => navigate('/reports')}>
        ← Back
      </Button>

      <h2 style={{ margin: '16px 0 4px', fontSize: 'var(--text-xl)' }}>
        {TARGET_LABEL[report.targetType ?? 0]} report
      </h2>
      <p style={{ margin: '0 0 20px', color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>
        {REASON_LABEL[report.reason ?? 0]}
      </p>

      <DataGrid items={items} />

      <h3 style={{ margin: '24px 0 8px', fontSize: 'var(--text-md)' }}>Reported content</h3>
      <ReportedContent report={report} />

      {isPending && <ReviewActions reportId={report.id!} onComplete={setReport} />}
    </div>
  );
}
